import React, { useState, useEffect } from 'react';
import axios from '@/utils/api';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { 
  X, Mail, Calendar, Settings, Trophy, Heart, Zap, Mic, Flame, 
  BookOpen, Clock, LogIn, RefreshCw, Crown, Star, Unlink, Building2, Globe 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDialog } from '@/context/DialogContext';

// Use same-origin API path on production custom domains
const API_BASE = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

const MEMBERSHIP_TIERS = [
  { value: 'founding_member', label: 'Founding Member', color: 'from-yellow-500 to-amber-600', icon: Crown },
  { value: 'premium', label: 'Premium', color: 'from-purple-500 to-pink-500', icon: Star },
  { value: 'free', label: 'Free', color: 'from-gray-500 to-gray-600', icon: null },
  { value: 'cancelled', label: '🚫 Cancelled', color: 'from-red-500 to-red-600', icon: null },
  { value: 'enterprise', label: 'Enterprise', color: 'from-blue-500 to-cyan-500', icon: Crown }
];

const REFERRAL_OPTIONS = [
  "Audria Richmond",
  "UnCloned Free Community", 
  "UnCloned Coaching",
  "UnCloned Consulting",
  "Instagram",
  "Facebook", 
  "LinkedIn",
  "YouTube",
  "TikTok",
  "Google Search",
  "Email Newsletter",
  "Podcast",
  "Live Event",
  "Webinar",
  "Friend Referral",
  "Facebook Ad",
  "Instagram Ad",
  "Other"
];

const BADGE_INFO = {
  first_entry: { name: 'First Entry', emoji: '📝', description: 'Created their first journal entry' },
  streak_3: { name: '3 Day Streak', emoji: '🔥', description: 'Journaled 3 days in a row' },
  streak_7: { name: 'Week Warrior', emoji: '⚡', description: 'Journaled 7 days in a row' },
  streak_30: { name: 'Month Master', emoji: '🏆', description: 'Journaled 30 days in a row' },
  entries_10: { name: '10 Entries', emoji: '📚', description: 'Created 10 journal entries' },
  entries_50: { name: '50 Entries', emoji: '📖', description: 'Created 50 journal entries' },
  first_voice: { name: 'Voice Pioneer', emoji: '🎤', description: 'Used voice typing for the first time' },
  mood_tracker: { name: 'Mood Tracker', emoji: '🎭', description: 'Tracked 5 different moods' }
};

const MOOD_EMOJIS = {
  uncloned: '🐑', magical: '✨', amazing: '🌟', loved: '💖',
  on_fire: '🔥', hopeful: '🌈', dreamy: '💫', free: '🦋',
  peaceful: '🌸', energized: '⚡', calm: '🌙', thoughtful: '💭',
  focused: '🎯', tired: '😴', melancholy: '🌧️', strong: '💪',
  angry: '😤', sad: '😢', grateful: '🙏', creative: '🎨'
};

const UserDetailModal = ({ userId, onClose }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
    referral_source: '',
    referral_other: ''
  });
  const [saving, setSaving] = useState(false);
  const { confirm } = useDialog();

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/admin/users/${userId}/details`);
        setDetails(res.data);
        setEditForm({
          first_name: res.data.first_name || '',
          last_name: res.data.last_name || '',
          phone_number: res.data.phone_number || '',
          referral_source: res.data.referral_source || '',
          referral_other: res.data.referral_other || ''
        });
      } catch (err) {
        toast.error('Failed to load user details');
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [userId]);

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/api/admin/users/${userId}`, editForm);
      setDetails(prev => ({
        ...prev,
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        phone_number: editForm.phone_number,
        referral_source: editForm.referral_source,
        referral_other: editForm.referral_other
      }));
      setIsEditing(false);
      toast.success('User updated successfully');
    } catch (err) {
      toast.error('Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const getMembershipDuration = () => {
    if (!details?.created_at) return 'Unknown';
    const created = new Date(details.created_at);
    const now = new Date();
    const diffMs = now - created;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day';
    if (diffDays < 7) return `${diffDays} days`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months`;
    return `${Math.floor(diffDays / 365)} years, ${Math.floor((diffDays % 365) / 30)} months`;
  };

  const handleResetBadges = async () => {
    const confirmed = await confirm({
      title: 'Reset Badges',
      message: 'Are you sure you want to reset all badges for this user? They will need to earn them again.',
      confirmText: 'Reset Badges',
      cancelText: 'Cancel',
      variant: 'destructive'
    });
    if (!confirmed) return;
    try {
      await axios.post(`${API_BASE}/api/admin/users/${userId}/reset-badges`);
      toast.success('Badges reset successfully!');
      const res = await axios.get(`${API_BASE}/api/admin/users/${userId}/details`);
      setDetails(res.data);
    } catch (err) {
      toast.error('Failed to reset badges');
    }
  };

  const handleUnlinkB2B = async () => {
    const confirmed = await confirm({
      title: 'Unlink from B2B Client',
      message: `Are you sure you want to unlink ${details.email} from their B2B organization (${details.client_name || details.client_id || 'Unknown'})? This will allow them to log into the main UnCloned Journal app again. Their data will be preserved.`,
      confirmText: 'Unlink User',
      cancelText: 'Cancel',
      variant: 'destructive'
    });
    if (!confirmed) return;
    try {
      await axios.post(`${API_BASE}/api/admin/users/unlink-b2b`, { email: details.email });
      toast.success(`${details.email} has been unlinked from B2B. They can now log into the main app.`);
      // Refresh the user details
      const res = await axios.get(`${API_BASE}/api/admin/users/${userId}/details`);
      setDetails(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlink user from B2B');
    }
  };

  const handleFullReset = async () => {
    const confirmed = await confirm({
      title: 'Delete All Entries',
      message: 'Are you sure you want to delete ALL entries for this user? This action cannot be undone.',
      confirmText: 'Delete All',
      cancelText: 'Cancel',
      variant: 'destructive'
    });
    if (!confirmed) return;
    try {
      await axios.post(`${API_BASE}/api/admin/users/${userId}/reset-all`);
      toast.success('All user data reset successfully!');
      const res = await axios.get(`${API_BASE}/api/admin/users/${userId}/details`);
      setDetails(res.data);
    } catch (err) {
      toast.error('Failed to reset user data');
    }
  };

  const handleChangeMembership = async (newTier) => {
    try {
      await axios.put(`${API_BASE}/api/admin/users/${userId}/set-tier`, { tier: newTier });
      setDetails(prev => ({ 
        ...prev, 
        membership_tier: newTier,
        subscription_cancelled: newTier === 'cancelled'
      }));
      toast.success(`Membership changed to ${newTier.replace('_', ' ')}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update membership');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4" style={{ zIndex: 9999 }} onClick={onClose}>
        <div className="animate-spin w-8 h-8 border-2 border-[#df1283] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!details) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4" style={{ zIndex: 9999 }} onClick={onClose}>
      <div 
        className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border-2 border-[#df1283] rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="user-detail-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#df1283] to-[#FF7901] flex items-center justify-center text-white text-2xl font-bold shadow-lg shadow-[#df1283]/30">
              {(details.first_name || details.display_name || details.name || 'U')[0].toUpperCase()}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {details.first_name && details.last_name 
                  ? `${details.first_name} ${details.last_name}` 
                  : details.display_name || details.name || 'Anonymous'}
              </h2>
              <p className="text-sm text-[#FDF2F8]/60 flex items-center gap-1">
                <Mail className="w-3 h-3" /> {details.email}
              </p>
              {details.phone_number && (
                <p className="text-sm text-[#FDF2F8]/60 flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {details.phone_number}
                </p>
              )}
              {details.referral_source && (
                <p className="text-sm text-[#22C55E] flex items-center gap-1 mt-1">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium">Found us via:</span> {details.referral_source}
                  {details.referral_other && ` - ${details.referral_other}`}
                </p>
              )}
              {!details.referral_source && (
                <p className="text-sm text-[#FDF2F8]/40 flex items-center gap-1 mt-1 italic">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Referral source not collected
                </p>
              )}
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <span className="text-xs text-[#FF7901] flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Joined {details.created_at ? format(new Date(details.created_at), 'MMM d, yyyy') : 'Unknown'}
                </span>
                <span className="text-xs bg-[#df1283]/20 text-[#df1283] px-2 py-0.5 rounded-full">
                  Member for {getMembershipDuration()}
                </span>
                {/* Auth Method Badge */}
                <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${
                  details.auth_type === 'google' 
                    ? 'bg-blue-500/20 text-blue-400' 
                    : details.auth_method === 'admin_created'
                    ? 'bg-purple-500/20 text-purple-400'
                    : 'bg-white/10 text-white/70'
                }`} data-testid="auth-method-badge">
                  {details.auth_type === 'google' ? (
                    <>
                      <svg className="w-3 h-3" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Google Sign-in
                    </>
                  ) : details.auth_method === 'admin_created' ? (
                    <>
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                      </svg>
                      Admin Created
                    </>
                  ) : (
                    <>
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Email Sign-up
                    </>
                  )}
                </span>
              </div>
              
              {/* Membership Tier Selector */}
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs text-[#FDF2F8]/60">Membership:</span>
                <select
                  value={details.membership_tier || 'founding_member'}
                  onChange={(e) => handleChangeMembership(e.target.value)}
                  className="bg-[#4A0A66]/50 border border-[#4A0A66] text-white text-xs px-2 py-1 rounded-lg focus:outline-none focus:border-[#df1283]"
                >
                  {MEMBERSHIP_TIERS.map(tier => (
                    <option key={tier.value} value={tier.value}>{tier.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-[#FDF2F8]/60 hover:text-white">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Edit Form */}
        {isEditing ? (
          <div className="bg-[#4A0A66]/30 border border-[#df1283]/50 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-[#df1283] mb-4 flex items-center gap-2">
              <Settings className="w-4 h-4" /> Edit User Details
            </h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs text-[#FDF2F8]/60 mb-1 block">First Name</label>
                <Input
                  value={editForm.first_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, first_name: e.target.value }))}
                  className="bg-[#1a0527] border-[#4A0A66] text-white"
                  placeholder="First Name"
                />
              </div>
              <div>
                <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Last Name</label>
                <Input
                  value={editForm.last_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, last_name: e.target.value }))}
                  className="bg-[#1a0527] border-[#4A0A66] text-white"
                  placeholder="Last Name"
                />
              </div>
            </div>
            <div className="mb-4">
              <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Phone Number</label>
              <Input
                value={editForm.phone_number}
                onChange={(e) => setEditForm(prev => ({ ...prev, phone_number: e.target.value }))}
                className="bg-[#1a0527] border-[#4A0A66] text-white"
                placeholder="Phone Number"
              />
            </div>
            <div className="mb-4">
              <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Referral Source</label>
              <select
                value={editForm.referral_source}
                onChange={(e) => setEditForm(prev => ({ ...prev, referral_source: e.target.value }))}
                className="w-full bg-[#1a0527] border border-[#4A0A66] text-white rounded-md px-3 py-2 text-sm"
              >
                <option value="">-- Select --</option>
                {REFERRAL_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            {editForm.referral_source === 'Other' && (
              <div className="mb-4">
                <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Please Specify</label>
                <Input
                  value={editForm.referral_other}
                  onChange={(e) => setEditForm(prev => ({ ...prev, referral_other: e.target.value }))}
                  className="bg-[#1a0527] border-[#4A0A66] text-white"
                  placeholder="How did they hear about us?"
                />
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)} className="text-[#FDF2F8]/60">
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveEdit} disabled={saving} className="bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-end mb-4">
            <Button size="sm" onClick={() => setIsEditing(true)} className="bg-[#4A0A66]/50 hover:bg-[#4A0A66] text-white border border-[#df1283]/30">
              <Settings className="w-4 h-4 mr-2" /> Edit User
            </Button>
          </div>
        )}

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-gradient-to-br from-[#df1283]/20 to-[#df1283]/5 border border-[#df1283]/30 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-[#df1283]">{details.total_entries}</p>
            <p className="text-xs text-[#FDF2F8]/60">Total Entries</p>
          </div>
          <div className="bg-gradient-to-br from-[#FF7901]/20 to-[#FF7901]/5 border border-[#FF7901]/30 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-[#FF7901]">{details.total_logins}</p>
            <p className="text-xs text-[#FDF2F8]/60">Total Logins</p>
          </div>
          <div className="bg-gradient-to-br from-green-500/20 to-green-500/5 border border-green-500/30 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-400">{details.current_streak}</p>
            <p className="text-xs text-[#FDF2F8]/60">Current Streak</p>
          </div>
          <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-500/5 border border-yellow-500/30 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-yellow-400">{details.badges_earned?.length || 0}</p>
            <p className="text-xs text-[#FDF2F8]/60">Badges Earned</p>
          </div>
        </div>

        {/* Badges Section */}
        <div className="bg-[#4A0A66]/20 border border-[#4A0A66]/40 rounded-lg p-4 mb-4">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-yellow-400" />
            Badges Unlocked
          </h3>
          {details.badges_earned && details.badges_earned.length > 0 ? (
            <div className="grid grid-cols-2 gap-2">
              {details.badges_earned.map(badge => (
                <div key={badge} className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-lg p-2 flex items-center gap-2">
                  <span className="text-2xl">{BADGE_INFO[badge]?.emoji || '🏆'}</span>
                  <div>
                    <p className="text-sm text-white font-medium">{BADGE_INFO[badge]?.name || badge}</p>
                    <p className="text-xs text-[#FDF2F8]/50">{BADGE_INFO[badge]?.description || ''}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#FDF2F8]/40 text-sm text-center py-2">No badges earned yet</p>
          )}
        </div>

        {/* Mood Distribution */}
        <div className="bg-[#4A0A66]/20 border border-[#4A0A66]/40 rounded-lg p-4 mb-4">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Heart className="w-4 h-4 text-[#df1283]" />
            Mood Distribution
          </h3>
          {details.mood_distribution && Object.keys(details.mood_distribution).length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {Object.entries(details.mood_distribution)
                .sort((a, b) => b[1] - a[1])
                .map(([mood, count]) => (
                  <div key={mood} className="bg-[#4A0A66]/40 rounded-lg px-3 py-2 flex items-center gap-2">
                    <span className="text-xl">{MOOD_EMOJIS[mood] || '😊'}</span>
                    <div>
                      <p className="text-xs text-white capitalize">{mood}</p>
                      <p className="text-sm font-bold text-[#df1283]">{count}</p>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <p className="text-[#FDF2F8]/40 text-sm text-center py-2">No mood data yet</p>
          )}
        </div>

        {/* Feature Usage */}
        <div className="bg-[#4A0A66]/20 border border-[#4A0A66]/40 rounded-lg p-4 mb-4">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#FF7901]" />
            Feature Usage
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center justify-between bg-[#4A0A66]/30 rounded-lg p-2">
              <span className="text-xs text-[#FDF2F8]/70 flex items-center gap-1">
                <Mic className="w-3 h-3 text-green-400" /> Voice Typing
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${details.has_voice ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                {details.has_voice ? '✓ Used' : 'Not Used'}
              </span>
            </div>
            <div className="flex items-center justify-between bg-[#4A0A66]/30 rounded-lg p-2">
              <span className="text-xs text-[#FDF2F8]/70 flex items-center gap-1">
                <Flame className="w-3 h-3 text-orange-400" /> Best Streak
              </span>
              <span className="text-xs font-bold text-[#FF7901]">{details.best_streak} days</span>
            </div>
            <div className="flex items-center justify-between bg-[#4A0A66]/30 rounded-lg p-2">
              <span className="text-xs text-[#FDF2F8]/70 flex items-center gap-1">
                <BookOpen className="w-3 h-3 text-[#df1283]" /> First Entry
              </span>
              <span className="text-xs text-[#FDF2F8]/60">{details.first_entry_date || 'None'}</span>
            </div>
            <div className="flex items-center justify-between bg-[#4A0A66]/30 rounded-lg p-2">
              <span className="text-xs text-[#FDF2F8]/70 flex items-center gap-1">
                <Clock className="w-3 h-3 text-purple-400" /> Last Entry
              </span>
              <span className="text-xs text-[#FDF2F8]/60">{details.last_entry_date || 'None'}</span>
            </div>
          </div>
        </div>

        {/* Login History */}
        <div className="bg-[#4A0A66]/20 border border-[#4A0A66]/40 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <LogIn className="w-4 h-4 text-purple-400" />
            Login History ({details.total_logins} total)
          </h3>
          {details.login_history && details.login_history.length > 0 ? (
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {details.login_history.slice(0, 10).map((ts) => {
                const date = new Date(ts);
                return (
                  <div key={ts} className="flex items-center justify-between text-xs">
                    <span className="text-[#FDF2F8]/50">
                      {date.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    <span className="text-[#FF7901]">
                      {date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-[#FDF2F8]/40 text-sm text-center py-2">No login history recorded yet</p>
          )}
        </div>

        {/* UTM Traffic Source Tracking - Always visible */}
        <div className="bg-[#4A0A66]/20 border border-[#4A0A66]/40 rounded-lg p-4" data-testid="user-detail-utm-section">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Globe className="w-4 h-4 text-[#0FFFE2]" />
            Traffic Source (UTM)
          </h3>
          {(details.utm_source || details.utm_medium || details.utm_campaign || details.utm_term || details.utm_content) ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {details.utm_source && (
                <div className="bg-[#4A0A66]/30 rounded-lg p-2" data-testid="detail-utm-source">
                  <div className="text-xs text-[#FDF2F8]/50 mb-0.5">Source</div>
                  <div className="text-sm text-white font-medium truncate" title={details.utm_source}>
                    {details.utm_source}
                  </div>
                </div>
              )}
              {details.utm_medium && (
                <div className="bg-[#4A0A66]/30 rounded-lg p-2" data-testid="detail-utm-medium">
                  <div className="text-xs text-[#FDF2F8]/50 mb-0.5">Medium</div>
                  <div className="text-sm text-white font-medium truncate" title={details.utm_medium}>
                    {details.utm_medium}
                  </div>
                </div>
              )}
              {details.utm_campaign && (
                <div className="bg-[#4A0A66]/30 rounded-lg p-2" data-testid="detail-utm-campaign">
                  <div className="text-xs text-[#FDF2F8]/50 mb-0.5">Campaign</div>
                  <div className="text-sm text-white font-medium truncate" title={details.utm_campaign}>
                    {details.utm_campaign}
                  </div>
                </div>
              )}
              {details.utm_term && (
                <div className="bg-[#4A0A66]/30 rounded-lg p-2" data-testid="detail-utm-term">
                  <div className="text-xs text-[#FDF2F8]/50 mb-0.5">Term</div>
                  <div className="text-sm text-white font-medium truncate" title={details.utm_term}>
                    {details.utm_term}
                  </div>
                </div>
              )}
              {details.utm_content && (
                <div className="bg-[#4A0A66]/30 rounded-lg p-2" data-testid="detail-utm-content">
                  <div className="text-xs text-[#FDF2F8]/50 mb-0.5">Content</div>
                  <div className="text-sm text-white font-medium truncate" title={details.utm_content}>
                    {details.utm_content}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-[#FDF2F8]/40 text-sm text-center py-2">No UTM tracking data available for this user</p>
          )}
        </div>

        {/* Status Badge */}
        {details.is_revoked && (
          <div className="mt-4 bg-red-500/20 border border-red-500/30 rounded-lg p-3 text-center">
            <p className="text-red-400 text-sm font-medium">⚠️ This user account is revoked</p>
          </div>
        )}
        
        {/* B2B Linked Status */}
        {(details.is_b2b_user || details.client_id) && (
          <div className="mt-4 bg-blue-500/20 border border-blue-500/30 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-blue-400" />
                <p className="text-blue-400 text-sm font-medium">
                  Linked to B2B: <span className="text-white">{details.client_name || details.client_id || 'Unknown Organization'}</span>
                </p>
              </div>
              <Button 
                onClick={handleUnlinkB2B} 
                variant="outline" 
                size="sm" 
                className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
              >
                <Unlink className="w-3 h-3 mr-1" />
                Unlink
              </Button>
            </div>
            <p className="text-xs text-blue-300/60 mt-1">
              This user cannot log into the main app while linked to a B2B organization.
            </p>
          </div>
        )}

        {/* Admin Actions */}
        <div className="mt-6 pt-4 border-t border-[#4A0A66]/40">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Settings className="w-4 h-4 text-[#FF7901]" />
            Admin Actions
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Button onClick={handleResetBadges} variant="outline" size="sm" className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20">
              <Trophy className="w-3 h-3 mr-1" />
              Reset Badges
            </Button>
            <Button onClick={handleFullReset} variant="outline" size="sm" className="border-orange-500/50 text-orange-400 hover:bg-orange-500/20">
              <RefreshCw className="w-3 h-3 mr-1" />
              Full Reset
            </Button>
            <Button onClick={onClose} variant="outline" size="sm" className="border-[#df1283]/50 text-[#df1283] hover:bg-[#df1283]/20">
              <X className="w-3 h-3 mr-1" />
              Close
            </Button>
          </div>
          <p className="text-xs text-[#FDF2F8]/40 mt-2">
            Reset Badges: Clears earned badges only. Full Reset: Clears badges + all entries.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UserDetailModal;
